// @ts-nocheck
import { MODULE_ACL, USER_ROLES } from "./constant";
import { isAclModuleOptionsRelevant } from "./aclModuleOptionsAccess";
import { parseModuleOperations } from "./voicePlanAcl";

export const AGENT_ACL_MODULE = MODULE_ACL.AGENTS;

/** Agent telephony type → ACL edit_option key */
export const AGENT_TYPE_TO_OPTION = {
  Mobile: "type_mobile",
  "Soft Phone": "type_soft_phone",
  WEBRTC: "type_webrtc",
};

export const AGENT_TYPE_OPTION_VALUES = Object.values(AGENT_TYPE_TO_OPTION);

export const AGENT_TYPE_SELECT_OPTIONS = [
  { label: "Mobile", value: "Mobile" },
  { label: "Soft Phone", value: "Soft Phone" },
  { label: "WEBRTC", value: "WEBRTC" },
];

export const AGENT_EDIT_OPTION_DEFS = [
  {
    value: "first_call_connect",
    label: "First call connect",
    description: "Set first call connect (agent / customer)",
  },
  {
    value: "outbound_enabled",
    label: "Outbound enabled",
    description: "Enable or disable outbound for the agent",
  },
  {
    value: "type_mobile",
    label: "Mobile",
    description: "Allow Mobile type when creating or editing agents",
  },
  {
    value: "type_soft_phone",
    label: "Soft Phone",
    description: "Allow Soft Phone type when creating or editing agents",
  },
  {
    value: "type_webrtc",
    label: "WEBRTC",
    description: "Allow WEBRTC type when creating or editing agents",
  },
];

const ALL_OPTION_VALUES = AGENT_EDIT_OPTION_DEFS.map((o) => o.value);

export const DEFAULT_AGENT_EDIT_OPTIONS = AGENT_EDIT_OPTION_DEFS.filter(
  (o) => o.defaultOn !== false
).map((o) => o.value);

function isSuperAdminRole(userRole, isSuperAdmin) {
  return isSuperAdmin || userRole === USER_ROLES.SUPERADMIN;
}

export function getAgentAclEntry(moduleOperations) {
  const ops = parseModuleOperations(moduleOperations);
  return ops[AGENT_ACL_MODULE] || null;
}

export function normalizeAgentEditOptions(options) {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => ALL_OPTION_VALUES.includes(o))
    ),
  ];
}

export function getAllowedAgentEditOptions(
  moduleOperations,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return ALL_OPTION_VALUES;
  }

  const entry = getAgentAclEntry(moduleOperations);
  if (!entry?.edit && !entry?.add) {
    return [];
  }

  const options = normalizeAgentEditOptions(entry.edit_options);
  if (options.length === 0) {
    return null;
  }

  return options;
}

export function canEditAgentOption(
  moduleOperations,
  optionKey,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }

  const allowed = getAllowedAgentEditOptions(moduleOperations, {
    userRole,
    isSuperAdmin,
  });
  if (allowed === null) {
    return true;
  }
  return allowed.includes(optionKey);
}

/**
 * Type ACL: if the ACL has no type_* options configured yet (legacy rows),
 * all types remain allowed. Once any type_* option is present, only those apply.
 */
export function canUseAgentType(
  moduleOperations,
  typeValue,
  { userRole = null, isSuperAdmin = false } = {}
) {
  const optionKey = AGENT_TYPE_TO_OPTION[typeValue];
  if (!optionKey) return false;

  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }

  const allowed = getAllowedAgentEditOptions(moduleOperations, {
    userRole,
    isSuperAdmin,
  });
  if (allowed === null) {
    return true;
  }

  const hasAnyTypeConfigured = AGENT_TYPE_OPTION_VALUES.some((v) =>
    allowed.includes(v)
  );
  if (!hasAnyTypeConfigured) {
    return true;
  }

  return allowed.includes(optionKey);
}

export function getAllowedAgentTypeSelectOptions(
  moduleOperations,
  { userRole = null, isSuperAdmin = false } = {}
) {
  return AGENT_TYPE_SELECT_OPTIONS.filter((o) =>
    canUseAgentType(moduleOperations, o.value, { userRole, isSuperAdmin })
  );
}

export function isAgentEditAclConfigured(moduleEntry) {
  if (!moduleEntry?.edit && !moduleEntry?.add) return false;
  return normalizeAgentEditOptions(moduleEntry.edit_options).length > 0;
}

export function getAgentEditOptionsForEditor(moduleOperations, isSuperAdmin) {
  const allowed = getAllowedAgentEditOptions(moduleOperations, { isSuperAdmin });
  if (isSuperAdmin || allowed === null) {
    return AGENT_EDIT_OPTION_DEFS;
  }
  return AGENT_EDIT_OPTION_DEFS.filter((o) => allowed.includes(o.value));
}

export function getParentGrantableAgentEditOptions(
  moduleOperations,
  isSuperAdmin
) {
  if (isSuperAdmin) return ALL_OPTION_VALUES;
  const allowed = getAllowedAgentEditOptions(moduleOperations, {
    isSuperAdmin: false,
  });
  if (allowed === null) {
    return ALL_OPTION_VALUES;
  }
  return allowed;
}

export function normalizeAgentEditOptionsForSave(
  options,
  parentGrantable = null
) {
  const pool =
    parentGrantable && parentGrantable.length > 0
      ? parentGrantable
      : ALL_OPTION_VALUES;
  return normalizeAgentEditOptions(options).filter((o) => pool.includes(o));
}

export function formatAgentEditOptionsLabel(moduleEntry) {
  if (!isAclModuleOptionsRelevant(moduleEntry)) return "—";
  const options = normalizeAgentEditOptions(moduleEntry.edit_options);
  if (options.length === 0) return "—";
  if (options.length === ALL_OPTION_VALUES.length) {
    return "All options";
  }
  return `${options.length} option(s)`;
}

export function getUserModuleOperations(user) {
  if (!user) return {};
  const raw =
    user?.acl_settings?.module_operations ?? user?.module_operations ?? null;
  return parseModuleOperations(raw);
}
